/**
 * FNP-1363 — BUILD THE ROLE IN FRONT OF THE CUSTOMER.
 *
 * ⚠️ THIS SCREEN IS THE TRUST MOMENT OF THE WHOLE PRODUCT, AND THAT IS WHY
 * THIS FILE EXISTS.
 *
 * A customer is being asked to create an IAM role in their own AWS account
 * that lets Flexi change their DNS — which is to say, take their website and
 * their email offline. Hesitating there is the correct instinct, not a failure
 * of nerve. The screen therefore has two jobs at once: as little friction as
 * possible, AND enough transparency to earn a yes. Those pull the same
 * direction: what settles a cautious person is seeing exactly what they are
 * granting, and being able to grant less.
 *
 * What this replaces was prose — "the Route 53 actions Flexi uses to read and
 * write your DNS records" — which names nothing and cannot be acted on. The
 * person careful enough to hesitate is precisely the person who needs the
 * literal action list.
 *
 * So: the customer ticks what they are willing to grant, and the policy is
 * assembled from those ticks. What they see is what they paste. The preview is
 * the explanation.
 *
 * ⚠️ THE TICK AND THE DECLARATION ARE ONE ACT, BY CONSTRUCTION.
 * Every grant here maps to the capability Flexi stores on the connection.
 * Today's single toggle declares a capability while showing nothing about the
 * permission it implies, so a customer can declare write and grant read — the
 * mismatch recorded in FNP-1357, live in dev, where a connection declares
 * `canWriteRecords: true` while assuming a role holding no write action at all.
 * When one checkbox drives both the pasted policy and the stored declaration,
 * they cannot disagree.
 *
 * ⚠️ ONE SOURCE FOR THE ACTION LIST. These arrays mirror the published contract
 * at https://docs.flexi.domains/connect-a-dns-provider.html. A second
 * hand-maintained copy drifts, and a customer follows whichever they found
 * first. `tests/rolePolicyBuilder.test.ts` pins them; the same discipline is
 * applied on the infrastructure side by `PUBLISHED_DOMAINS_INTEGRATION_POLICY`
 * in `flexinet-shared-svcs-iam-infra`.
 */

/**
 * One thing a customer can choose to let Flexi do.
 *
 * ⚠️ CREATE AND DELETE ARE SEPARATE CHOICES, DELIBERATELY.
 *
 * They are different risk decisions and a customer may reasonably take
 * different positions on them: "you may create a hosted zone for me" and "you
 * may delete one of my hosted zones" are not the same sentence, and neither
 * are "you may request a certificate" and "you may remove a certificate my
 * site is serving". Bundling them forces someone comfortable with the first to
 * accept the second, and the likely answer to that is no to both.
 *
 * The cost of the split is that a withheld delete leaves the product partly
 * unable to act, so each such grant states what stops working — see
 * `withholdingCost`. The customer decides with that in front of them rather
 * than discovering it at a failed action months later.
 */
export type RoleGrantKey =
    | 'readDns'
    | 'writeRecords'
    | 'createZones'
    | 'deleteZones'
    | 'requestCertificates'
    | 'deleteCertificates';

export type RoleGrantSelection = Readonly<Record<RoleGrantKey, boolean>>;

/**
 * ⚠️ READ IS NOT OPTIONAL, and this is a fact about the product rather than a
 * default we chose. Connect-time validation probes `listZones({limit: 1})`, so
 * a role that cannot list zones cannot validate — a connection without these
 * actions can never leave INVALID. Offering it as a choice would offer a
 * choice that does not work.
 */
export const ALWAYS_GRANTED: RoleGrantKey = 'readDns';

interface GrantDefinition {
    readonly key: RoleGrantKey;
    /** Shown beside the checkbox, in the customer's terms and not ours. */
    readonly label: string;
    /** One line on what it buys them. */
    readonly description: string;
    /** The literal IAM actions this grant adds. */
    readonly actions: readonly string[];
    /**
     * ⚠️ WHAT STOPS WORKING IF THIS IS WITHHELD, said before they decide.
     *
     * A customer declining a permission is exercising a choice the product
     * offers them, so the product owes them the consequence up front rather
     * than at a failed action months later. Absent where withholding costs
     * nothing beyond the obvious.
     */
    readonly withholdingCost?: string;
    /**
     * Destructive grants are the ones customers most reasonably refuse, and
     * the UI marks them so the decision is visibly weightier than the others.
     */
    readonly destructive?: boolean;
}

/**
 * ⚠️ ORDERED LEAST TO MOST PRIVILEGED, deliberately. A customer reading down
 * the list meets "read my zones" before "delete my zones", so the shape of
 * what they are agreeing to builds up rather than arriving all at once.
 */
export const ROLE_GRANTS: readonly GrantDefinition[] = [
    {
        key: 'readDns',
        label: 'Read your DNS zones and records',
        description:
            'Required to connect at all — {product} lists one zone to check the '
            + 'credential works. On its own this grants no ability to change anything.',
        actions: [
            'route53:ListHostedZones',
            'route53:GetHostedZone',
            'route53:ListResourceRecordSets',
        ],
    },
    {
        key: 'writeRecords',
        label: 'Change records in your zones',
        description:
            'Lets {product} add, edit and remove records. Leave this off to start '
            + 'read-only and turn it on later by re-validating.',
        actions: ['route53:ChangeResourceRecordSets'],
    },
    {
        key: 'createZones',
        label: 'Create new hosted zones',
        description:
            'Lets {product} create a hosted zone when you add a domain it does not '
            + 'already have one for.',
        actions: ['route53:CreateHostedZone'],
        withholdingCost:
            'Without this you create hosted zones yourself in AWS first, then '
            + 'manage them through {product}.',
    },
    {
        key: 'deleteZones',
        label: 'Delete hosted zones',
        description:
            'Lets {product} remove a hosted zone when you delete the domain it '
            + 'belongs to.',
        actions: ['route53:DeleteHostedZone'],
        destructive: true,
        withholdingCost:
            'Without this, deleting a domain in {product} leaves its hosted zone '
            + 'behind in your AWS account for you to remove — {product} will say so '
            + 'rather than failing silently. Many customers prefer this.',
    },
    {
        key: 'requestCertificates',
        label: 'Request TLS certificates',
        description:
            'Certificates are issued in YOUR AWS account under this role. {product} '
            + 'holds no certificate permission of its own, so without this {product} '
            + 'cannot issue one for you.',
        // ⚠️ Describe rides with request rather than being its own tick: it is
        // read-only, and a certificate you cannot read the status of is one you
        // cannot be told has been issued. Splitting it would offer a choice
        // whose "no" produces a broken certificate view, not a safer one.
        actions: ['acm:RequestCertificate', 'acm:DescribeCertificate'],
        withholdingCost:
            'Without this you issue certificates yourself and {product} will not '
            + 'offer to request them.',
    },
    {
        key: 'deleteCertificates',
        label: 'Remove TLS certificates',
        description:
            'Lets {product} delete a certificate it issued, once nothing is using it.',
        actions: ['acm:DeleteCertificate'],
        destructive: true,
        withholdingCost:
            'Without this, certificates {product} issued stay in your AWS account '
            + 'when you remove them here, and you delete them yourself. {product} '
            + 'checks with the provider before deleting anything, so a '
            + 'certificate still in use is never removed either way.',
    },
] as const;

/**
 * The selection a first-time customer starts from.
 *
 * ⚠️ NOTHING DESTRUCTIVE IS PRE-TICKED, and that is the whole posture. This is
 * the moment a customer decides whether to trust Flexi with their DNS at all,
 * and a pre-ticked "delete my hosted zones" is exactly the box that turns a
 * hesitant yes into a no. Asking for the least that works is both the honest
 * default and the one most likely to be accepted.
 *
 * Delete is not lost by defaulting off — it is requested at the moment it is
 * actually needed, with the reason attached, which is a far better position to
 * ask from than a blank connect form.
 */
export const DEFAULT_GRANTS: RoleGrantSelection = {
    readDns: true,
    writeRecords: true,
    createZones: true,
    deleteZones: false,
    requestCertificates: true,
    deleteCertificates: false,
};

/** Read-only, offered as a one-click path rather than five unticks. */
export const READ_ONLY_GRANTS: RoleGrantSelection = {
    readDns: true,
    writeRecords: false,
    createZones: false,
    deleteZones: false,
    requestCertificates: false,
    deleteCertificates: false,
};

/**
 * The selection a STORED connection represents — what its tenant declared.
 *
 * ⚠️ FNP-1469 — ONE DEFINITION, BECAUSE TWO WOULD DRIFT. The correction drawer
 * seeded this inline to populate its checkboxes; the connection page now needs
 * the same mapping to render the permission policy for a saved connection. Two
 * copies of "which stored field is which tick" is two chances to disagree, and
 * a policy document that disagreed with the checkboxes would be a third source
 * of truth for the same fact.
 *
 * ⚠️ `readDns` IS ALWAYS TRUE and is not read from the record. It is
 * `ALWAYS_GRANTED` — required to connect at all, never offered as a choice, and
 * so never stored as one. Reading it from a field that does not exist would
 * seed false and silently drop the read actions from a rendered policy.
 *
 * ⚠️ EVERY OTHER GRANT FAILS CLOSED, through `declaredDnsGrant` and
 * `declaredCertificateGrant`. An absent block, an absent field and an explicit
 * false are all "not granted" here — which is the right direction for a
 * document telling a tenant what to put in an IAM policy. Over-stating a grant
 * would have them widen a role further than they agreed to.
 */
// ⚠️ NULLABLE ALL THE WAY DOWN, BECAUSE THE API IS. Both blocks and every
// field inside them arrive as `boolean | null | undefined` — GraphQL sends null
// for a field it has no value for, and the block itself is absent on a
// connection predating the schema. A signature admitting only `undefined`
// compiles against a fixture and fails against the real type, which is how this
// was caught. The `=== true` tests below make all three shapes fail closed.
export const declaredSelectionOf = (
    connection: {
        dnsCapabilities?: {
            canWriteRecords?: boolean | null,
            canCreateZones?: boolean | null,
            canDeleteZones?: boolean | null,
        } | null,
        certificateCapabilities?: {
            canRequest?: boolean | null,
            canDelete?: boolean | null,
        } | null,
    },
): RoleGrantSelection => ({
    readDns: true,
    writeRecords: connection.dnsCapabilities?.canWriteRecords === true,
    createZones: connection.dnsCapabilities?.canCreateZones === true,
    deleteZones: connection.dnsCapabilities?.canDeleteZones === true,
    requestCertificates: connection.certificateCapabilities?.canRequest === true,
    deleteCertificates: connection.certificateCapabilities?.canDelete === true,
});

/**
 * The IAM actions a selection grants, in the order the definitions declare
 * them — stable, so the preview does not reshuffle as boxes are ticked.
 */
export function actionsFor(selection: RoleGrantSelection): string[] {
    return ROLE_GRANTS
        .filter(grant => grant.key === ALWAYS_GRANTED || selection[grant.key])
        .flatMap(grant => [...grant.actions]);
}

/**
 * The permission policy, as the customer will paste it.
 *
 * ⚠️ TWO STATEMENTS WHEN BOTH SERVICES ARE PRESENT, matching how the published
 * document presents them. A customer grants DNS and certificates
 * independently, and one merged statement hides that they are separable.
 *
 * `Resource: '*'` is the document's own shape, and the reason is real: a
 * connection points at whatever zones that customer has, so a baked list would
 * exclude every zone created later, and `acm:RequestCertificate` creates a
 * resource that does not exist yet and cannot be scoped at all.
 */
export function permissionPolicyFor(selection: RoleGrantSelection): string {
    const route53 = ROLE_GRANTS
        .filter(g => g.key === ALWAYS_GRANTED || selection[g.key])
        .flatMap(g => g.actions.filter(a => a.startsWith('route53:')));
    const acm = ROLE_GRANTS
        .filter(g => g.key === ALWAYS_GRANTED || selection[g.key])
        .flatMap(g => g.actions.filter(a => a.startsWith('acm:')));

    const statements = [];
    if (route53.length > 0) {
        statements.push({Effect: 'Allow', Action: route53, Resource: '*'});
    }
    if (acm.length > 0) {
        statements.push({Effect: 'Allow', Action: acm, Resource: '*'});
    }

    return JSON.stringify({Version: '2012-10-17', Statement: statements}, null, 2);
}

/**
 * The trust policy, as the customer will paste it.
 *
 * ⚠️ THE EXTERNAL ID CANNOT BE KNOWN YET, AND SAYING SO IS THE POINT. It is
 * minted from the connection id, so it does not exist until the connection is
 * saved. The placeholder is therefore not a gap in this function — it is the
 * two-step flow the drawer already explains, made visible in the artifact the
 * customer is about to write.
 *
 * ⚠️ `principal` IS NOT OPTIONAL AND MUST NOT BE INVENTED HERE. It is Flexi's
 * own account, which only the backend knows. The app must never hardcode it
 * per stage: that is a second source of truth that drifts silently, and a
 * trust policy naming the wrong principal fails in a way that looks like the
 * customer's mistake. When the API does not supply it, render the guidance
 * rather than a guess — see `TRUST_POLICY_PRINCIPAL_UNAVAILABLE`.
 */
export function trustPolicyFor(
    principal: string,
    externalId?: string,
): string {
    return JSON.stringify({
        Version: '2012-10-17',
        Statement: [{
            Effect: 'Allow',
            Principal: {AWS: principal},
            Action: 'sts:AssumeRole',
            Condition: {
                StringEquals: {
                    'sts:ExternalId': externalId ?? '<the external ID {product} shows you after you save>',
                },
            },
        }],
    }, null, 2);
}

/**
 * The fallback when the principal is not in hand AT RENDER TIME.
 *
 * ⚠️ NO LONGER THE PERMANENT STATE. FNP-1428 added `flexiPrincipal` to the API
 * and `FormComponent` reads it through `useFlexiPrincipalQuery`, so the drawer
 * normally renders a complete, pasteable trust policy. This constant covers the
 * cases that remain: the query in flight, or a deployment whose API predates the
 * field.
 *
 * ⚠️ STILL NEVER A HARDCODED GUESS. The reasoning on `trustPolicyFor` holds —
 * a per-stage constant in the app is a second source of truth that drifts, and
 * a trust policy naming the wrong principal fails in a way that looks like the
 * customer's mistake. Absent the real value, say so and link to the document.
 */
export const TRUST_POLICY_PRINCIPAL_UNAVAILABLE = 'PRINCIPAL_NOT_AVAILABLE';

/**
 * The capability declaration these ticks imply.
 *
 * ⚠️ THIS IS THE HALF THAT FIXES FNP-1357. The same checkbox that puts an
 * action into the pasted policy sets the capability Flexi stores, so a
 * connection cannot claim a permission its role was never granted. Any future
 * caller that sets the declaration from somewhere other than this function
 * reintroduces the mismatch.
 *
 * ⚠️ FNP-1399 — ONE FIELD PER TICK. The three DNS answers are returned
 * separately because the customer gave three separate answers; see the body.
 */
export function declarationFor(selection: RoleGrantSelection): {
    dns: { canWriteRecords: boolean; canCreateZones: boolean; canDeleteZones: boolean };
    certificate: { canRequest: boolean; canDelete: boolean };
} {
    return {
        // ⚠️ FNP-1399 — ONE FIELD PER CHECKBOX, NOT AN OR OF ALL THREE.
        //
        // This used to be `writable: writeRecords || createZones ||
        // deleteZones`, and the API stored that single boolean. A customer who
        // ticked only "Change records" was therefore recorded as able to
        // DELETE THEIR HOSTED ZONES — the one grant whose false positive
        // destroys data, handed out because the model could not express the
        // narrower answer.
        //
        // The customer answers three questions on the connect drawer. Storing
        // three answers is the whole fix; folding them here would put the
        // defect back at its source, whatever the API stores.
        dns: {
            canWriteRecords: selection.writeRecords,
            canCreateZones: selection.createZones,
            canDeleteZones: selection.deleteZones,
        },
        certificate: {
            canRequest: selection.requestCertificates,
            // ⚠️ SEPARATE FROM canRequest, because the customer decided them
            // separately. FNP-1280 split these two fields for exactly this
            // case: a tenant may issue certificates through Flexi and still
            // keep deletion to themselves, and collapsing them would declare a
            // capability their policy does not grant.
            canDelete: selection.deleteCertificates,
        },
    };
}

/**
 * The zone-lifecycle halves, for callers that need them apart.
 *
 * `writable` is one flag over a surface the customer now splits four ways, so
 * anything reasoning about a specific action should ask here rather than infer
 * from it. `canCreateZones` and `canDeleteZones` already exist on the stored
 * capability block; this is what should populate them.
 */
export function zoneCapabilitiesFor(selection: RoleGrantSelection): {
    canCreateZones: boolean;
    canDeleteZones: boolean;
} {
    return {
        canCreateZones: selection.createZones,
        canDeleteZones: selection.deleteZones,
    };
}
